import React, { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import { Card, Pill } from "../../components/common/Primitives";
import { Button } from "../../components/ui/button";
import {
  Loader2, Sparkles, TrendingUp, TrendingDown, Eye, Bookmark,
  Handshake, DoorOpen, XCircle, Award, ArrowUpRight, ArrowDownRight,
  FileDown,
} from "lucide-react";
import api, { formatApiError } from "../../lib/api";

const numFmt = (v) => (v == null ? "—" : Number(v).toLocaleString("en-IN"));

const StatTile = ({ icon: Icon, label, value, sublabel, testId, gradient }) => (
  <div
    className={
      "rounded-lg p-4 border relative overflow-hidden " +
      (gradient
        ? "border-gold/40 bg-gradient-to-br from-navy/[0.02] to-gold/[0.08]"
        : "border-navy/[0.08] bg-white")
    }
    data-testid={testId}
  >
    <div className="flex items-center gap-2 mb-2">
      <div className={"p-1.5 rounded-md " + (gradient ? "bg-gold/20" : "bg-navy/[0.05]")}>
        <Icon size={14} className={gradient ? "text-gold" : "text-navy"} />
      </div>
      <div className="overline text-ink-muted">{label}</div>
    </div>
    <div className="font-heading text-3xl text-ink">{value}</div>
    {sublabel && <div className="text-[11px] text-ink-muted font-mono mt-1">{sublabel}</div>}
  </div>
);

const Sparkline = ({ data, height = 60 }) => {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data.map((d) => d.events), 1);
  const width = 100;
  const step = width / Math.max(data.length - 1, 1);
  const points = data
    .map((d, i) => {
      const x = i * step;
      const y = height - (d.events / max) * (height - 4) - 2;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");
  const areaPoints = `0,${height} ${points} ${width},${height}`;
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="w-full"
      style={{ height: `${height}px` }}
      preserveAspectRatio="none"
      data-testid="premium-analytics-sparkline"
    >
      <defs>
        <linearGradient id="sparkFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FFD700" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#FFD700" stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={areaPoints} fill="url(#sparkFill)" />
      <polyline
        points={points}
        fill="none"
        stroke="#0F3D2E"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export default function StartupPremiumAnalytics() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    api
      .get("/startup/founder-premium/analytics")
      .then((r) => setData(r.data))
      .catch((e) => {
        setError(formatApiError(e));
        toast.error(formatApiError(e));
      });
  }, []);

  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const resp = await api.get("/startup/founder-premium/analytics/pdf", {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `venturaz-premium-roi-${(data?.startup_name || "board-deck").replace(/\s+/g, "-").toLowerCase()}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Board deck PDF downloaded");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (error) {
    return (
      <DashboardShell title="Premium Analytics" subtitle="STARTUP · ROI DASHBOARD">
        <Toaster richColors position="top-right" />
        <Card testId="premium-analytics-locked">
          <div className="text-center py-8">
            <Award size={40} className="mx-auto text-gold mb-3" />
            <div className="font-heading text-xl text-ink">Founder Premium required</div>
            <div className="text-sm text-ink-muted mt-2 mb-4">{error}</div>
            <Button
              onClick={() => (window.location.href = "/startup/founder-premium")}
              data-testid="premium-analytics-upgrade-btn"
            >
              Upgrade to unlock →
            </Button>
          </div>
        </Card>
      </DashboardShell>
    );
  }

  if (!data) {
    return (
      <DashboardShell title="Premium Analytics" subtitle="STARTUP · ROI DASHBOARD">
        <div className="text-ink-muted">
          <Loader2 className="animate-spin inline mr-2" size={14} /> Loading…
        </div>
      </DashboardShell>
    );
  }

  const funnel = data.funnel || {};
  const uplift = data.pct_uplift_vs_baseline;

  return (
    <DashboardShell title="Premium Analytics" subtitle="STARTUP · ROI DASHBOARD">
      <Toaster richColors position="top-right" />

      {/* Premium hero */}
      <div
        className="rounded-lg p-6 mb-6 border-2 border-gold bg-gradient-to-br from-navy via-navy/95 to-gold/[0.15] text-white shadow-2xl relative overflow-hidden"
        data-testid="premium-analytics-hero"
      >
        <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-gold/10 blur-3xl -mr-32 -mt-32" />
        <div className="relative z-10 flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 bg-gold text-navy px-2 py-1 rounded text-[10px] font-mono uppercase tracking-widest mb-3">
              <Sparkles size={10} /> Founder Premium · Private
            </div>
            <h2 className="font-heading text-3xl md:text-4xl leading-tight">
              Your Premium ROI
            </h2>
            <p className="text-sm text-white/70 mt-2 max-w-xl">
              Every view, save, interest and deal room your startup has attracted since Founder
              Premium activated. Numbers you can take back to your board.
            </p>
          </div>
          <div className="text-right">
            <div className="overline text-gold">Days Active</div>
            <div className="font-heading text-5xl text-white mt-1">
              {data.days_active ?? 0}
            </div>
            <div className="text-[11px] font-mono text-white/60 mt-0.5">
              Since {data.activated_at?.slice(0, 10)}
            </div>
            <button
              onClick={downloadPdf}
              disabled={downloadingPdf}
              data-testid="premium-analytics-pdf-btn"
              className="mt-4 inline-flex items-center gap-2 bg-gold text-navy px-3 py-2 rounded-md text-xs font-heading hover:bg-gold/90 disabled:opacity-50 transition"
            >
              {downloadingPdf ? (
                <Loader2 className="animate-spin" size={12} />
              ) : (
                <FileDown size={12} />
              )}
              Download Board-Deck PDF
            </button>
          </div>
        </div>
      </div>

      {/* Uplift ribbon */}
      {uplift !== null && uplift !== undefined && (
        <div
          className={
            "flex items-center gap-3 rounded-md p-4 mb-6 border " +
            (uplift >= 0
              ? "bg-emerald-50 border-emerald-500/40 text-emerald-900"
              : "bg-rose-50 border-rose-500/40 text-rose-900")
          }
          data-testid="premium-analytics-uplift"
        >
          {uplift >= 0 ? <ArrowUpRight size={20} /> : <ArrowDownRight size={20} />}
          <div className="flex-1">
            <div className="overline text-inherit opacity-70">Engagement Uplift vs Baseline</div>
            <div className="font-heading text-xl">
              {uplift >= 0 ? "+" : ""}
              {uplift}% since activation
            </div>
          </div>
          <div className="text-right text-xs font-mono opacity-70">
            baseline {data.baseline_events_30d_pre} · current {data.total_events}
          </div>
        </div>
      )}

      {/* Stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <StatTile
          testId="stat-views"
          icon={Eye}
          label="Views"
          value={numFmt(funnel.view || 0)}
          sublabel="Investor detail loads"
          gradient
        />
        <StatTile
          testId="stat-saves"
          icon={Bookmark}
          label="Saves"
          value={numFmt(funnel.save || 0)}
          sublabel="Added to watchlist"
        />
        <StatTile
          testId="stat-interests"
          icon={Handshake}
          label="Interests"
          value={numFmt(data.interests || 0)}
          sublabel="Formal interest expressed"
          gradient
        />
        <StatTile
          testId="stat-dealrooms"
          icon={DoorOpen}
          label="Deal Rooms"
          value={numFmt(data.deal_rooms || 0)}
          sublabel="Advanced conversations"
        />
        <StatTile
          testId="stat-rejections"
          icon={XCircle}
          label="Rejections"
          value={numFmt(data.rejections || 0)}
          sublabel="With diagnostic reason"
        />
        <StatTile
          testId="stat-unique-investors"
          icon={Sparkles}
          label="Unique Investors"
          value={numFmt(data.unique_investors || 0)}
          sublabel="Touched your profile"
          gradient
        />
      </div>

      {/* Timeseries chart */}
      <Card testId="premium-analytics-timeseries" className="mb-6 border-gold/30">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="overline">Engagement — Last 30 Days</div>
            <div className="text-xs text-ink-muted mt-0.5">
              Every logged event bucketed by day
            </div>
          </div>
          <TrendingUp size={16} className="text-gold" />
        </div>
        <Sparkline data={data.timeseries_daily || []} height={80} />
        <div className="flex justify-between text-[10px] font-mono text-ink-muted mt-2">
          <span>{data.timeseries_daily?.[0]?.day}</span>
          <span>{data.timeseries_daily?.[data.timeseries_daily.length - 1]?.day}</span>
        </div>
      </Card>

      {/* Rejection reason breakdown */}
      {data.rejection_reasons_top && data.rejection_reasons_top.length > 0 && (
        <Card testId="premium-analytics-rejections" className="mb-6">
          <div className="overline mb-3">Rejection Reasons — top {data.rejection_reasons_top.length}</div>
          <div className="space-y-2">
            {data.rejection_reasons_top.map((r) => (
              <div
                key={r.reason}
                className="flex items-center justify-between border border-navy/[0.06] rounded p-3"
                data-testid={`rejection-row-${r.reason.replace(/\s+/g, "_")}`}
              >
                <div>
                  <div className="text-sm text-ink">{r.reason}</div>
                  <div className="text-[10px] font-mono text-ink-muted uppercase tracking-wider mt-0.5">
                    Fix this to unlock the next round of conversations
                  </div>
                </div>
                <Pill tone="danger">{r.count}× cited</Pill>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="text-center text-[11px] font-mono text-ink-muted italic">
        Analytics private to your team. VenturaZ staff cannot see this dashboard.
      </div>
    </DashboardShell>
  );
}
